import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for LetWise
 * Provides dynamic meta tags, Open Graph, Twitter Cards, and structured data
 * 
 * Usage:
 * <SEO 
 *   title="Page Title" 
 *   description="Page description"
 *   path="/page-path"
 *   type="website" // or "article" for blog posts
 * />
 */

const BASE_URL = 'https://letwise.io';
const DEFAULT_IMAGE = `${BASE_URL}/logo512.png`;
const SITE_NAME = 'LetWise';

const SEO = ({
  title,
  description = 'LetWise is a modern property lettings platform that reduces costs, improves transparency, and automates property management, inspections, and repairs.',
  path = '/',
  type = 'website',
  image = DEFAULT_IMAGE,
  noindex = false,
  structuredData = null,
  article = null,
  breadcrumbs = null,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Modern Property Lettings Management Platform`;
  const url = `${BASE_URL}${path}`;
  
  // Generate breadcrumb structured data
  const breadcrumbData = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${BASE_URL}${crumb.path}`
    }))
  } : null;
  
  // Article structured data for blog posts
  const articleData = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": image,
    "author": {
      "@type": "Organization",
      "name": SITE_NAME
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "logo": {
        "@type": "ImageObject",
        "url": DEFAULT_IMAGE
      }
    },
    "datePublished": article.publishedDate,
    "dateModified": article.modifiedDate || article.publishedDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    }
  } : null;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_GB" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@letwise_io" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {breadcrumbData && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbData)}
        </script>
      )}
      
      {articleData && (
        <script type="application/ld+json">
          {JSON.stringify(articleData)}
        </script>
      )}
    </Helmet>
  );
};

// Predefined SEO configurations for common pages
export const SEOConfig = {
  home: {
    title: null, // Uses default
    description: 'LetWise is a modern property lettings platform that reduces costs, improves transparency, and automates property management, inspections, and repairs. Full management from just 6%.',
    path: '/',
  },
  forLandlords: {
    title: 'For Landlords',
    description: 'Full property management services for landlords at just 6%. Tenant finding, rent collection, maintenance coordination, compliance management, and smart inspections.',
    path: '/for-landlords',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'For Landlords', path: '/for-landlords' }
    ]
  },
  forTenants: {
    title: 'For Tenants',
    description: 'Find your perfect rental home with LetWise. Create a reusable renter profile, apply to properties easily, and manage your tenancy online.',
    path: '/for-tenants',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'For Tenants', path: '/for-tenants' }
    ]
  },
  properties: {
    title: 'Search Properties',
    description: 'Browse available rental properties across the UK. Use our map view to search by location, filter by price, bedrooms, and property type.',
    path: '/properties',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Properties', path: '/properties' }
    ]
  },
  howItWorks: {
    title: 'How It Works',
    description: 'Learn how LetWise simplifies property management for landlords, tenants, and contractors. Sign up, add your property, and let us handle the rest.',
    path: '/how-it-works',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'How It Works', path: '/how-it-works' }
    ]
  },
  rentersRightsAct: {
    title: "Renters' Rights Act 2025",
    description: "Everything you need to know about the Renters' Rights Act 2025. Key changes, effective dates, Section 21 abolition, and how LetWise ensures compliance.",
    path: '/renters-rights-act',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: "Renters' Rights Act 2025", path: '/renters-rights-act' }
    ]
  },
  inspections: {
    title: 'Property Inspections',
    description: 'Smart property inspections powered by advanced technology. Baseline scans, check-in/check-out inspections, and automated damage detection.',
    path: '/inspections',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Inspections', path: '/inspections' }
    ]
  },
  contractors: {
    title: 'For Contractors',
    description: 'Join LetWise as a contractor. Get access to maintenance jobs, grow your business, and get paid promptly.',
    path: '/contractors',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'For Contractors', path: '/contractors' }
    ]
  },
  forAgents: {
    title: 'For Letting Agents',
    description: 'Scale your lettings agency with LetWise technology. White-label solutions, portfolio management, and compliance automation.',
    path: '/for-agents',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'For Agents', path: '/for-agents' }
    ]
  },
};

export default SEO;
